import { observer } from "mobx-react-lite"
import { useState } from "react"
import { useConfig } from "wagmi"

import type { ManualClPositionStore } from "@/domains/thick-v1/positions"
import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { THICK_V1_BASE_ADDRESSES } from "@/domains/thick-v1/constants"
import { collectFeesFromThickNfpInteraction } from "@/domains/thick-v1/interactions"
import useEventEmitter from "@/hooks/useEventEmitter"

/**
 * Row-level "Claim fees" composite for the my-positions table.
 *
 * Wraps the thick-v1 collect-fees interaction. The interaction publishes
 * `ManualClFeesCollectedEvent` on success, which the position orchestrator
 * subscribes to and uses to refresh both the GraphQL and on-chain queries —
 * so this component never needs to imperatively refetch anything.
 *
 * Account and chainId are read from the `scaleApp` root store rather than
 * wagmi hooks (per architect.mdc — `useAccount` / `useChainConfig` are
 * forbidden in composites). The wagmi `Config` is still retrieved via
 * `useConfig()` because it is framework plumbing rather than domain state.
 */
export const ClaimFeesButton = observer(
  ({ position, disabled }: { position: ManualClPositionStore; disabled: boolean }) => {
    const scaleApp = useScaleApplication()
    const config = useConfig()
    const toastEmitter = useEventEmitter()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const connectedAddress = scaleApp.Wallet.Connection.connectedAddress?.raw
    const chainId = scaleApp.Config.chainId
    const nfpmPending = position.nfpmTokenIdPending

    const onClick = async () => {
      if (!connectedAddress) return
      if (position.nfpmTokenId === undefined) return
      setIsSubmitting(true)
      try {
        await collectFeesFromThickNfpInteraction({
          account: connectedAddress,
          chainId,
          config,
          tokenId: position.nfpmTokenId,
          poolAddress: position.pool.address.raw,
          nfpManagerAddress: THICK_V1_BASE_ADDRESSES.nfPositionManager,
          emitter: toastEmitter ?? scaleApp.ManualClV1.Emitter,
          eventBus: scaleApp.EventBus,
        })
      } finally {
        setIsSubmitting(false)
      }
    }

    const label = isSubmitting ? "Claiming..." : nfpmPending ? "Resolving NFP id…" : "Claim"

    return (
      <Button
        variant={"neutral"}
        size={"md"}
        className="mx-auto w-fit"
        onClick={onClick}
        disabled={disabled || isSubmitting || !connectedAddress || nfpmPending}
        title={nfpmPending ? "On-chain NFP id is still resolving for this position" : undefined}
      >
        {label}
      </Button>
    )
  },
)
